import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import type { ChangeOrder, ProposalBuilderData } from '../types';
import type { PurchaseJobPlBucket, PurchaseRequisition } from '../types/purchasing';
import { db } from '../utils/firebase';
import { subscribeChangeOrdersForDeal } from '../utils/changeOrders';
import { jobNodeProposalBudget, jobNodeQbActual } from '../utils/jobPurchaseBudget';
import { summarizeJobNode, type JobNodeBudgetSummary } from '../utils/purchaseBudget';
import { PURCHASE_JOB_PL_BUCKETS } from '../utils/purchasingSpendClass';
import { useJobPurchasePlActualInputs } from './useJobPurchasePlActualInputs';

export type DealPurchaseBudgetNodeRow = {
  bucket: PurchaseJobPlBucket;
  label: string;
  budgetUsd: number;
  summary: JobNodeBudgetSummary;
};

export function useDealPurchaseBudgetByNode(
  dealId: string | undefined,
  rows: PurchaseRequisition[],
): { loaded: boolean; nodes: DealPurchaseBudgetNodeRow[] } {
  const [proposal, setProposal] = useState<ProposalBuilderData | null>(null);
  const [proposalReady, setProposalReady] = useState(!dealId);
  const [changeOrders, setChangeOrders] = useState<ChangeOrder[]>([]);
  const plActual = useJobPurchasePlActualInputs(dealId);

  useEffect(() => {
    if (!dealId) {
      setProposal(null);
      setProposalReady(true);
      return;
    }
    setProposalReady(false);
    return onSnapshot(
      doc(db, 'proposalBuilders', dealId),
      snap => {
        setProposal(snap.exists() ? ({ id: snap.id, ...(snap.data() as object) } as ProposalBuilderData) : null);
        setProposalReady(true);
      },
      () => {
        setProposal(null);
        setProposalReady(true);
      },
    );
  }, [dealId]);

  useEffect(() => {
    if (!dealId) {
      setChangeOrders([]);
      return;
    }
    return subscribeChangeOrdersForDeal(dealId, setChangeOrders);
  }, [dealId]);

  const loaded = !dealId || proposalReady;

  const nodes = useMemo(() => {
    if (!dealId || !loaded) return [];
    const plContext = {
      overrides: plActual.overrides,
      designerInvoiceJobCash: plActual.designerInvoiceJobCash,
    };
    return PURCHASE_JOB_PL_BUCKETS.map(({ id: bucket, label }) => {
      const budgetUsd = jobNodeProposalBudget(proposal, changeOrders, bucket);
      const actualUsd = jobNodeQbActual(plActual.expenses, dealId, bucket, plContext);
      const summary = summarizeJobNode({
        rows,
        dealId,
        plBucket: bucket,
        budgetUsd,
        actualUsd,
      });
      return { bucket, label, budgetUsd, summary };
    }).filter(
      row =>
        // Nonzero, not positive: a CO credit can drive a bucket negative and
        // the budget line must still appear (as a reduction) rather than vanish.
        Math.abs(row.budgetUsd) > 0.005
        || row.summary.actualUsd > 0.005
        || row.summary.reservedUsd > 0.005
        || row.summary.committedUsd > 0.005,
    );
  }, [dealId, loaded, proposal, changeOrders, plActual, rows]);

  return { loaded, nodes };
}
